import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  Stack
} from '@chakra-ui/react'
import { TextareaFormControl } from 'BrokerTsLib/src/components'
import type { EditOrganizationUnit, OrganizationUnit } from 'BrokerTsLib/src/dtos'
import {
  getOrganizationUnitTypeName,
  LegalRelationshipTypes,
  VwfsSourceTypes,
} from 'BrokerTsLib/src/enums'
import { useGetPossibleTypes } from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import type { Ref } from 'react'
import { forwardRef, useMemo } from 'react'

import { InputGroup } from '@/components'
import { FormGroup } from '@/components/FormGroup'
import { SwitchInput } from '@/components/SwitchInput'
import type { FormSectionProps } from '@/containers/forms/FormSection'
import { FormSection } from '@/containers/forms/FormSection'
import { usePartner } from '@/context/PartnerContext'

export const OrganizationInfoFormSection = forwardRef(
  function OrganizationInfoFormSection(
    {
      isCreate,
      organization,
      ...rest
    }: {
      isCreate: boolean | undefined
      organization: OrganizationUnit
    } & FormSectionProps,
    ref: Ref<HTMLDivElement>,
  ) {
    const { currentPartner } = usePartner()
    const {
      control,
      register,
      formState: { errors },
    } = useFormContext<EditOrganizationUnit>()

    const { data } = useGetPossibleTypes(
      organization.id,
      organization.superiorOrganizationUnitId,
    )

    const possibleTypes = useMemo(
      () =>
        Array.from(
          new Set(
            isCreate
              ? [...(data?.data ?? [])]
              : [organization.type, ...(data?.data ?? [])],
          ),
        ),
      [isCreate, organization, data?.data],
    )

    return (
      <FormSection ref={ref} sectionTitle="Informace o organizaci" {...rest}>
        <Stack gap={6}>
          <FormGroup>
            <FormControl
              isRequired
              isInvalid={!!errors.name}
              variant="fullWidth"
            >
              <FormLabel htmlFor="name">Název</FormLabel>
              <InputGroup
                id="name"
                placeholder="Název organizace"
                errors={errors.name}
                {...register('name', {
                  required: 'Zadejte název organizace',
                  maxLength: 256,
                })}
              />
            </FormControl>

            <FormControl
              variant="fullWidth"
              isRequired
              isInvalid={!!errors.type}
            >
              <FormLabel>Typ organizace</FormLabel>

              <Select
                {...register('type', {
                  valueAsNumber: true,
                  validate: (value) =>
                    !value && value !== 0 ? 'Vyberte typ organizace' : true,
                })}
                isDisabled={!isCreate && possibleTypes.length === 1}
              >
                {isCreate && <option value="">Vyberte typ organizace</option>}
                {possibleTypes.map((type) => (
                  <option key={type} value={type}>
                    {getOrganizationUnitTypeName(type)}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>
                {errors.type && errors.type.message}
              </FormErrorMessage>
            </FormControl>

            {!isCreate && (
              <FormControl alignSelf={'end'} mb={3}>
                <SwitchInput size="md" {...register('valid')}>
                  Platná
                </SwitchInput>
              </FormControl>
            )}
          </FormGroup>

          {currentPartner?.partner === 'Jap' && (
            <FormControl variant="fullWidth">
              <FormLabel>Právní vztah</FormLabel>

              <Select {...register('properties.legalRelationship')}>
                <option value="">Vyberte typ právního vztahu</option>
                {LegalRelationshipTypes.map((type) => (
                  <option key={type[0]} value={type[0]}>
                    {type[1]}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>
                {errors.properties?.legalRelationship &&
                  errors.properties.legalRelationship.message}
              </FormErrorMessage>
            </FormControl>
          )}

          {currentPartner?.partner === 'VWFS' && (
            <FormControl variant="fullWidth">
              <FormLabel>Zdroj</FormLabel>

              <Select {...register('properties.vwfsSource')}>
                <option value="">Vyberte zdroj</option>
                {VwfsSourceTypes.map((type) => (
                  <option key={type[0]} value={type[0]}>
                    {type[1]}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>
                {errors.properties?.vwfsSource &&
                  errors.properties.vwfsSource.message}
              </FormErrorMessage>
            </FormControl>
          )}

          <TextareaFormControl label="Popis"
            control={control} name='description'
            rules={{ maxLength: 2000 }} />

          <FormGroup>
            <FormControl variant="fullWidth">
              <FormLabel>IČO</FormLabel>

              <InputGroup
                placeholder="Identifikační číslo firmy"
                pattern="\d+"
                {...register('ico', { maxLength: 20 })}
              />
            </FormControl>

            <FormControl variant="fullWidth">
              <FormLabel>Kód ČNB</FormLabel>

              <InputGroup
                placeholder="Kód přidělený ČNB"
                {...register('nationalBankLicenseCode', { maxLength: 256 })}
              />
            </FormControl>
          </FormGroup>

          <FormGroup>
            <FormControl flex={2}>
              <FormLabel>Ulice</FormLabel>

              <InputGroup
                placeholder="Např. Dlouhá"
                htmlSize={24}
                {...register('addressStreet', { maxLength: 100 })}
              />
            </FormControl>

            <FormControl flex={1}>
              <FormLabel>Č.p. a č.o.</FormLabel>

              <InputGroup
                placeholder="Např. 123"
                htmlSize={6}
                {...register('addressStreetNumber', { maxLength: 10 })}
              />
            </FormControl>

            <FormControl flex={1}>
              <FormLabel>PSČ</FormLabel>

              <InputGroup
                placeholder="Např. 110 00"
                htmlSize={6}
                pattern="[\d\s]+"
                {...register('addressPostCode', { maxLength: 20 })}
              />
            </FormControl>

            <FormControl flex={2}>
              <FormLabel>Město</FormLabel>

              <InputGroup
                placeholder="Např. Praha"
                htmlSize={12}
                {...register('addressCity', { maxLength: 100 })}
              />
            </FormControl>
          </FormGroup>

          <FormControl>
            <FormLabel>Kontaktní osoba</FormLabel>

            <InputGroup
              placeholder="Např. Jan Novák"
              {...register('contactName', { maxLength: 100 })}
            />
          </FormControl>

          <FormGroup>
            <FormControl variant="fullWidth">
              <FormLabel>Telefon</FormLabel>

              <Input {...register('phone', { maxLength: 20 })} />
            </FormControl>

            <FormControl variant="fullWidth">
              <FormLabel>Mobilní telefon</FormLabel>

              <Input {...register('mobilePhone', { maxLength: 20 })} />
            </FormControl>

            <FormControl variant="fullWidth">
              <FormLabel>E-mail</FormLabel>

              <InputGroup
                type="email"
                placeholder="Např. jan.novak@cebia.cz"
                {...register('email', {
                  maxLength: 256,
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'Zadejte platnou e-mailovou adresu',
                  },
                })}
              />
            </FormControl>
          </FormGroup>
        </Stack>
      </FormSection>
    )
  },
)
